/**
 * Renderer - Canvas 2D Rendering System
 * 
 * Handles all canvas drawing operations with support for interpolation
 * between physics frames for smooth rendering.
 */

import { PhysicsWorld, PhysicsBody } from './PhysicsWorld';
import anime9372233Url from '../assets/anime/pixabay/girl-9372233.png';
import anime9372829Url from '../assets/anime/pixabay/girl-9372829.png';
import anime9564350Url from '../assets/anime/pixabay/girl-9564350.png';
import anime9532623Url from '../assets/anime/pixabay/transparent-9532623.png';
import anime9532619Url from '../assets/anime/pixabay/transparent-9532619.png';
import anime9344274Url from '../assets/anime/pixabay/anime-9344274.png';

/** Colors matching the game's visual style */
export const Colors = {
    background: '#0e1021',
    box: '#ffb6d9',
    bag: '#a6d9ff',
    triangle: '#c7a9ff',
    wall: '#3c3f7a',
    conveyor: '#1f233f',
    highlight: '#ffe66d',
    fillLine: '#7d83c7',
};

/** Render state for interpolation */
interface RenderState {
    x: number;
    y: number;
    angle: number;
}

interface CharacterPalette {
    card: string;
    hair: string;
    outfit: string;
    accent: string;
    skin: string;
}

/**
 * Renderer class - manages canvas drawing
 */
export class Renderer {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private width = 0;
    private height = 0;
    private dpr = 1;

    // Previous frame positions for interpolation
    private prevStates: Map<number, RenderState> = new Map();
    private currStates: Map<number, RenderState> = new Map();
    private characterImages: HTMLImageElement[] = [];

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Could not get 2D rendering context');
        }
        this.ctx = ctx;
        this.loadCharacterImages();
        this.resize();
    }

    /** Handle window resize */
    resize(): void {
        this.dpr = window.devicePixelRatio || 1;
        this.width = window.innerWidth;
        this.height = window.innerHeight;

        // Set canvas size accounting for device pixel ratio
        this.canvas.width = this.width * this.dpr;
        this.canvas.height = this.height * this.dpr;
        this.canvas.style.width = `${this.width}px`;
        this.canvas.style.height = `${this.height}px`;

        // Scale context for DPR
        this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    }

    /** Get canvas dimensions */
    getDimensions(): { width: number; height: number } {
        return { width: this.width, height: this.height };
    }

    /** Store current physics state for next frame interpolation */
    captureState(physics: PhysicsWorld): void {
        // Move current to previous
        this.prevStates = this.currStates;
        this.currStates = new Map();

        // Capture current positions
        for (const body of physics.getAllBodies()) {
            const pos = physics.getPosition(body.handle);
            const angle = physics.getRotation(body.handle);
            if (pos !== null && angle !== null) {
                this.currStates.set(body.handle, { x: pos.x, y: pos.y, angle });
            }
        }
    }

    /** Get interpolated position for smooth rendering */
    private getInterpolatedState(handle: number, alpha: number): RenderState | null {
        const curr = this.currStates.get(handle);
        const prev = this.prevStates.get(handle);

        if (!curr) return null;
        if (!prev) return curr;

        // Lerp position
        const x = prev.x + (curr.x - prev.x) * alpha;
        const y = prev.y + (curr.y - prev.y) * alpha;

        // Lerp angle (handle wrap-around)
        let angleDiff = curr.angle - prev.angle;
        if (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
        if (angleDiff < -Math.PI) angleDiff += Math.PI * 2;
        const angle = prev.angle + angleDiff * alpha;

        return { x, y, angle };
    }

    /** Clear the canvas */
    clear(): void {
        this.ctx.fillStyle = Colors.background;
        this.ctx.fillRect(0, 0, this.width, this.height);

        const topGlow = this.ctx.createLinearGradient(0, 0, 0, this.height);
        topGlow.addColorStop(0, 'rgba(170, 115, 255, 0.18)');
        topGlow.addColorStop(0.45, 'rgba(109, 231, 255, 0.10)');
        topGlow.addColorStop(1, 'rgba(14, 16, 33, 0)');
        this.ctx.fillStyle = topGlow;
        this.ctx.fillRect(0, 0, this.width, this.height);
    }

    /** Render all physics bodies */
    renderBodies(physics: PhysicsWorld, alpha: number, heldBodyHandle?: number): void {
        for (const body of physics.getAllBodies()) {
            const state = this.getInterpolatedState(body.handle, alpha);
            if (!state) continue;

            const isHeld = body.handle === heldBodyHandle;
            this.renderBody(body, state, isHeld);
        }
    }

    /** Render a single body */
    private renderBody(body: PhysicsBody, state: RenderState, isHeld: boolean): void {
        const { userData } = body;
        const { x, y, angle } = state;

        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.rotate(angle);

        // Draw based on shape type
        switch (userData.shapeType) {
            case 'box':
                this.renderBox(userData.width ?? 50, userData.height ?? 50, isHeld, userData.id);
                break;
            case 'bag':
                this.renderBag(
                    userData.width ?? 50,
                    userData.height ?? 50,
                    userData.chamferRadius ?? 10,
                    isHeld,
                    userData.id
                );
                break;
            case 'triangle':
                this.renderTriangle(userData.radius ?? 30, isHeld, userData.id);
                break;
        }

        this.ctx.restore();
    }

    /** Render a rectangular idol card */
    private renderBox(width: number, height: number, isHeld: boolean, seed: number): void {
        const halfW = width / 2;
        const halfH = height / 2;
        const palette = this.getCharacterPalette(seed);

        this.renderHeldFrame(-halfW, -halfH, width, height, isHeld, 0);

        this.ctx.fillStyle = palette.card;
        this.ctx.fillRect(-halfW, -halfH, width, height);
        this.ctx.strokeStyle = '#2b2552';
        this.ctx.lineWidth = 1.5;
        this.ctx.strokeRect(-halfW, -halfH, width, height);

        this.renderCharacterCard(width, height, palette, 'square', seed);
    }

    /** Render a rounded idol card */
    private renderBag(width: number, height: number, radius: number, isHeld: boolean, seed: number): void {
        const halfW = width / 2;
        const halfH = height / 2;
        const r = Math.min(radius, halfW, halfH);
        const palette = this.getCharacterPalette(seed + 17);

        this.renderHeldFrame(-halfW, -halfH, width, height, isHeld, r);

        this.ctx.fillStyle = palette.card;
        this.roundRect(-halfW, -halfH, width, height, r);
        this.ctx.fill();

        this.ctx.strokeStyle = '#2b2552';
        this.ctx.lineWidth = 1.5;
        this.roundRect(-halfW, -halfH, width, height, r);
        this.ctx.stroke();

        this.renderCharacterCard(width, height, palette, 'rounded', seed);
    }

    /** Render a triangular idol standee */
    private renderTriangle(radius: number, isHeld: boolean, seed: number): void {
        const points = [
            { x: 0, y: -radius },
            { x: radius * Math.cos(Math.PI / 6), y: radius * 0.5 },
            { x: -radius * Math.cos(Math.PI / 6), y: radius * 0.5 }
        ];
        const palette = this.getCharacterPalette(seed + 31);

        if (isHeld) {
            this.ctx.strokeStyle = Colors.highlight;
            this.ctx.lineWidth = 3;
            this.ctx.shadowColor = 'rgba(255, 230, 109, 0.55)';
            this.ctx.shadowBlur = 12;
            this.tracePolygon(points);
            this.ctx.stroke();
            this.ctx.shadowBlur = 0;
        }

        this.ctx.fillStyle = palette.card;
        this.tracePolygon(points);
        this.ctx.fill();

        this.ctx.strokeStyle = '#2b2552';
        this.ctx.lineWidth = 1.5;
        this.tracePolygon(points);
        this.ctx.stroke();

        this.ctx.save();
        this.tracePolygon(points);
        this.ctx.clip();
        const hasPortrait = this.drawCharacterImage(-radius * 0.78, -radius * 0.95, radius * 1.56, radius * 1.65, seed + 31);
        if (!hasPortrait) {
            this.ctx.fillStyle = palette.outfit;
            this.ctx.beginPath();
            this.ctx.moveTo(0, radius * 0.02);
            this.ctx.lineTo(radius * 0.42, radius * 0.56);
            this.ctx.lineTo(-radius * 0.42, radius * 0.56);
            this.ctx.closePath();
            this.ctx.fill();

            this.ctx.fillStyle = palette.skin;
            this.ctx.beginPath();
            this.ctx.arc(0, -radius * 0.14, radius * 0.24, 0, Math.PI * 2);
            this.ctx.fill();

            this.renderHair(0, -radius * 0.21, radius * 0.5, radius * 0.4, palette.hair);
            this.drawFace(0, -radius * 0.14, radius * 0.16, palette.accent);
            this.drawRibbon(0, radius * 0.1, radius * 0.26, palette.accent);
        } else {
            this.ctx.fillStyle = 'rgba(11, 15, 33, 0.08)';
            this.ctx.fillRect(-radius, -radius, radius * 2, radius * 2);
        }
        this.drawTriangleSparkles(radius, palette.accent);
        this.ctx.restore();
    }

    private renderHeldFrame(x: number, y: number, width: number, height: number, isHeld: boolean, radius: number): void {
        if (!isHeld) return;

        this.ctx.strokeStyle = Colors.highlight;
        this.ctx.lineWidth = 3;
        this.ctx.shadowColor = 'rgba(255, 230, 109, 0.55)';
        this.ctx.shadowBlur = 12;

        if (radius <= 0) {
            this.ctx.strokeRect(x - 2, y - 2, width + 4, height + 4);
        } else {
            this.roundRect(x - 2, y - 2, width + 4, height + 4, radius + 2);
            this.ctx.stroke();
        }

        this.ctx.shadowBlur = 0;
    }

    private renderCharacterCard(
        width: number,
        height: number,
        palette: CharacterPalette,
        variant: 'square' | 'rounded',
        seed: number
    ): void {
        const halfW = width / 2;
        const halfH = height / 2;
        const inset = Math.max(3, Math.min(width, height) * 0.05);
        const faceY = -halfH + height * 0.4;
        const faceR = Math.min(width, height) * 0.18;
        const portraitX = -halfW + inset;
        const portraitY = -halfH + inset;
        const portraitW = width - inset * 2;
        const portraitH = height - inset * 2;

        const bgGradient = this.ctx.createLinearGradient(0, portraitY, 0, portraitY + portraitH);
        bgGradient.addColorStop(0, 'rgba(255,255,255,0.14)');
        bgGradient.addColorStop(1, 'rgba(12, 15, 34, 0.22)');
        this.ctx.fillStyle = bgGradient;
        if (variant === 'rounded') {
            this.roundRect(portraitX, portraitY, portraitW, portraitH, 10);
            this.ctx.fill();
        } else {
            this.ctx.fillRect(portraitX, portraitY, portraitW, portraitH);
        }

        this.ctx.save();
        if (variant === 'rounded') {
            this.roundRect(portraitX, portraitY, portraitW, portraitH, 10);
            this.ctx.clip();
        } else {
            this.ctx.beginPath();
            this.ctx.rect(portraitX, portraitY, portraitW, portraitH);
            this.ctx.clip();
        }
        const hasPortrait = this.drawCharacterImage(portraitX, portraitY, portraitW, portraitH, seed);
        this.ctx.restore();

        if (!hasPortrait) {
            this.ctx.fillStyle = palette.outfit;
            if (variant === 'rounded') {
                this.roundRect(-width * 0.32, faceY + faceR * 0.15, width * 0.64, height * 0.34, 10);
                this.ctx.fill();
            } else {
                this.ctx.fillRect(-width * 0.3, faceY + faceR * 0.15, width * 0.6, height * 0.32);
            }

            this.ctx.fillStyle = palette.skin;
            this.ctx.beginPath();
            this.ctx.arc(0, faceY, faceR, 0, Math.PI * 2);
            this.ctx.fill();

            this.renderHair(0, faceY - faceR * 0.55, faceR * 2.15, faceR * 1.55, palette.hair);
            this.drawFace(0, faceY, faceR * 0.68, palette.accent);
            this.drawRibbon(0, faceY + faceR * 1.16, faceR * 0.84, palette.accent);
        }

        const portraitShade = this.ctx.createLinearGradient(0, portraitY, 0, portraitY + portraitH);
        portraitShade.addColorStop(0, 'rgba(255,255,255,0.05)');
        portraitShade.addColorStop(0.45, 'rgba(0,0,0,0)');
        portraitShade.addColorStop(1, 'rgba(8,10,25,0.12)');
        this.ctx.fillStyle = portraitShade;
        if (variant === 'rounded') {
            this.roundRect(portraitX, portraitY, portraitW, portraitH, 10);
            this.ctx.fill();
        } else {
            this.ctx.fillRect(portraitX, portraitY, portraitW, portraitH);
        }

        this.ctx.fillStyle = 'rgba(11, 15, 33, 0.72)';
        if (variant === 'rounded') {
            this.roundRect(-halfW + inset, halfH - height * 0.16, width - inset * 2, height * 0.10, 8);
            this.ctx.fill();
        } else {
            this.ctx.fillRect(-halfW + inset, halfH - height * 0.16, width - inset * 2, height * 0.10);
        }
        this.drawRibbon(0, halfH - height * 0.11, faceR * 0.42, palette.accent);
        this.drawCardSparkles(width, height, palette.accent);
    }

    private loadCharacterImages(): void {
        const urls = [
            anime9372233Url,
            anime9372829Url,
            anime9564350Url,
            anime9532623Url,
            anime9532619Url,
            anime9344274Url,
        ];

        this.characterImages = urls.map((url) => {
            const img = new Image();
            img.decoding = 'async';
            img.src = url;
            return img;
        });
    }

    private drawCharacterImage(x: number, y: number, width: number, height: number, seed: number): boolean {
        const selection = this.getBestCharacterImageForFrame(width, height, seed);
        const img = selection?.image ?? null;
        if (!img || !img.complete || img.naturalWidth <= 0 || img.naturalHeight <= 0) {
            return false;
        }

        const srcW = img.naturalWidth;
        const srcH = img.naturalHeight;

        this.ctx.save();
        this.ctx.translate(x + width / 2, y + height / 2);

        const rotated = selection?.rotate90 ?? false;
        if (rotated) {
            this.ctx.rotate(Math.PI / 2);
        }

        const frameW = rotated ? height : width;
        const frameH = rotated ? width : height;
        const scale = Math.min(frameW / srcW, frameH / srcH);
        const drawW = srcW * scale;
        const drawH = srcH * scale;

        this.ctx.shadowColor = 'rgba(0, 0, 0, 0.28)';
        this.ctx.shadowBlur = Math.max(4, Math.min(width, height) * 0.08);
        this.ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
        this.ctx.shadowBlur = 0;
        this.ctx.restore();
        return true;
    }

    private getCharacterImage(seed: number): HTMLImageElement | null {
        if (this.characterImages.length === 0) return null;
        const img = this.characterImages[Math.abs(seed) % this.characterImages.length];
        return img ?? null;
    }

    private getBestCharacterImageForFrame(
        width: number,
        height: number,
        seed: number
    ): { image: HTMLImageElement; rotate90: boolean } | null {
        if (this.characterImages.length === 0) return null;

        const targetAspect = width / height;
        const startIndex = Math.abs(seed) % this.characterImages.length;
        let best: { image: HTMLImageElement; rotate90: boolean; score: number } | null = null;

        for (let offset = 0; offset < this.characterImages.length; offset++) {
            const index = (startIndex + offset) % this.characterImages.length;
            const image = this.characterImages[index];
            if (!image || !image.complete || image.naturalWidth <= 0 || image.naturalHeight <= 0) {
                continue;
            }

            const aspect = image.naturalWidth / image.naturalHeight;
            const normalScore = Math.abs(Math.log(Math.max(0.01, aspect / targetAspect)));
            const rotatedAspect = image.naturalHeight / image.naturalWidth;
            const rotatedScore = Math.abs(Math.log(Math.max(0.01, rotatedAspect / targetAspect)));

            const preferRotation = targetAspect > 1.15 && rotatedScore + 0.03 < normalScore;
            const rotate90 = preferRotation;
            const score = rotate90 ? rotatedScore : normalScore;

            if (!best || score < best.score) {
                best = { image, rotate90, score };
            }
        }

        if (!best) {
            const fallback = this.getCharacterImage(seed);
            return fallback ? { image: fallback, rotate90: false } : null;
        }

        return { image: best.image, rotate90: best.rotate90 };
    }

    private drawFace(x: number, y: number, size: number, accent: string): void {
        const eyeOffsetX = size * 0.58;
        const eyeY = y - size * 0.08;

        this.ctx.fillStyle = '#162040';
        this.ctx.beginPath();
        this.ctx.ellipse(x - eyeOffsetX, eyeY, size * 0.14, size * 0.2, 0, 0, Math.PI * 2);
        this.ctx.ellipse(x + eyeOffsetX, eyeY, size * 0.14, size * 0.2, 0, 0, Math.PI * 2);
        this.ctx.fill();

        this.ctx.fillStyle = '#ffffff';
        this.ctx.beginPath();
        this.ctx.arc(x - eyeOffsetX + size * 0.04, eyeY - size * 0.04, size * 0.04, 0, Math.PI * 2);
        this.ctx.arc(x + eyeOffsetX + size * 0.04, eyeY - size * 0.04, size * 0.04, 0, Math.PI * 2);
        this.ctx.fill();

        this.ctx.strokeStyle = accent;
        this.ctx.lineWidth = Math.max(1, size * 0.08);
        this.ctx.beginPath();
        this.ctx.moveTo(x - size * 0.18, y + size * 0.34);
        this.ctx.quadraticCurveTo(x, y + size * 0.48, x + size * 0.18, y + size * 0.34);
        this.ctx.stroke();

        this.ctx.fillStyle = 'rgba(255, 134, 170, 0.45)';
        this.ctx.beginPath();
        this.ctx.arc(x - size * 0.88, y + size * 0.16, size * 0.15, 0, Math.PI * 2);
        this.ctx.arc(x + size * 0.88, y + size * 0.16, size * 0.15, 0, Math.PI * 2);
        this.ctx.fill();
    }

    private renderHair(x: number, y: number, width: number, height: number, hairColor: string): void {
        this.ctx.fillStyle = hairColor;
        this.ctx.beginPath();
        this.ctx.moveTo(x - width * 0.5, y + height * 0.24);
        this.ctx.quadraticCurveTo(x - width * 0.44, y - height * 0.34, x, y - height * 0.38);
        this.ctx.quadraticCurveTo(x + width * 0.44, y - height * 0.34, x + width * 0.5, y + height * 0.24);
        this.ctx.quadraticCurveTo(x + width * 0.34, y + height * 0.08, x + width * 0.24, y + height * 0.42);
        this.ctx.quadraticCurveTo(x, y + height * 0.6, x - width * 0.24, y + height * 0.42);
        this.ctx.quadraticCurveTo(x - width * 0.34, y + height * 0.08, x - width * 0.5, y + height * 0.24);
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.strokeStyle = 'rgba(255,255,255,0.24)';
        this.ctx.lineWidth = 1;
        this.ctx.beginPath();
        this.ctx.moveTo(x, y - height * 0.28);
        this.ctx.lineTo(x - width * 0.08, y + height * 0.2);
        this.ctx.moveTo(x + width * 0.12, y - height * 0.2);
        this.ctx.lineTo(x + width * 0.04, y + height * 0.26);
        this.ctx.stroke();
    }

    private drawRibbon(x: number, y: number, size: number, color: string): void {
        this.ctx.fillStyle = color;

        this.ctx.beginPath();
        this.ctx.moveTo(x - size, y);
        this.ctx.lineTo(x - size * 0.2, y - size * 0.35);
        this.ctx.lineTo(x - size * 0.2, y + size * 0.35);
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.beginPath();
        this.ctx.moveTo(x + size, y);
        this.ctx.lineTo(x + size * 0.2, y - size * 0.35);
        this.ctx.lineTo(x + size * 0.2, y + size * 0.35);
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.fillStyle = '#fff2a9';
        this.ctx.beginPath();
        this.ctx.arc(x, y, size * 0.2, 0, Math.PI * 2);
        this.ctx.fill();
    }

    private drawCardSparkles(width: number, height: number, color: string): void {
        const sparkles = [
            { x: -width * 0.33, y: -height * 0.22, size: Math.min(width, height) * 0.08 },
            { x: width * 0.31, y: -height * 0.04, size: Math.min(width, height) * 0.06 },
            { x: -width * 0.24, y: height * 0.22, size: Math.min(width, height) * 0.05 },
        ];
        this.drawSparkles(sparkles, color);
    }

    private drawTriangleSparkles(radius: number, color: string): void {
        const sparkles = [
            { x: -radius * 0.32, y: -radius * 0.45, size: radius * 0.07 },
            { x: radius * 0.3, y: -radius * 0.08, size: radius * 0.06 },
            { x: 0, y: radius * 0.33, size: radius * 0.05 },
        ];
        this.drawSparkles(sparkles, color);
    }

    private drawSparkles(points: Array<{ x: number; y: number; size: number }>, color: string): void {
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = 1.2;
        for (const sparkle of points) {
            this.ctx.beginPath();
            this.ctx.moveTo(sparkle.x - sparkle.size, sparkle.y);
            this.ctx.lineTo(sparkle.x + sparkle.size, sparkle.y);
            this.ctx.moveTo(sparkle.x, sparkle.y - sparkle.size);
            this.ctx.lineTo(sparkle.x, sparkle.y + sparkle.size);
            this.ctx.stroke();
        }
    }

    private tracePolygon(points: ReadonlyArray<{ x: number; y: number }>): void {
        this.ctx.beginPath();
        const first = points[0];
        if (!first) return;
        this.ctx.moveTo(first.x, first.y);
        for (let i = 1; i < points.length; i++) {
            const p = points[i];
            if (p) this.ctx.lineTo(p.x, p.y);
        }
        this.ctx.closePath();
    }

    private getCharacterPalette(seed: number): CharacterPalette {
        const palettes: readonly CharacterPalette[] = [
            { card: '#ffd0e6', hair: '#3c2c7d', outfit: '#ff6fae', accent: '#6de7ff', skin: '#ffd9bf' },
            { card: '#cfe9ff', hair: '#22356f', outfit: '#5ec2ff', accent: '#ff8ec7', skin: '#ffe0c7' },
            { card: '#eee0ff', hair: '#5b2b86', outfit: '#b57dff', accent: '#ffd166', skin: '#ffd8ba' },
            { card: '#d8fff2', hair: '#0d5b54', outfit: '#28d8b2', accent: '#ff7aa2', skin: '#ffe2cb' },
            { card: '#fff0cc', hair: '#9a4b1f', outfit: '#ffb84a', accent: '#7c8dff', skin: '#ffd7b9' },
        ];
        const palette = palettes[Math.abs(seed) % palettes.length];
        if (!palette) {
            throw new Error('Character palette list is empty');
        }
        return palette;
    }

    /** Helper to draw rounded rectangle path */
    private roundRect(x: number, y: number, w: number, h: number, r: number): void {
        this.ctx.beginPath();
        this.ctx.moveTo(x + r, y);
        this.ctx.lineTo(x + w - r, y);
        this.ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        this.ctx.lineTo(x + w, y + h - r);
        this.ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        this.ctx.lineTo(x + r, y + h);
        this.ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        this.ctx.lineTo(x, y + r);
        this.ctx.quadraticCurveTo(x, y, x + r, y);
        this.ctx.closePath();
    }

    /** Render container walls */
    renderContainer(
        floorY: number,
        leftWallX: number,
        rightWallX: number,
        wallThickness: number,
        containerTop: number
    ): void {
        this.ctx.fillStyle = Colors.wall;

        // Floor
        this.ctx.fillRect(leftWallX, floorY, rightWallX - leftWallX, wallThickness);

        // Left wall
        this.ctx.fillRect(leftWallX - wallThickness, containerTop, wallThickness, floorY - containerTop + wallThickness);

        // Right wall
        this.ctx.fillRect(rightWallX, containerTop, wallThickness, floorY - containerTop + wallThickness);

        // Idol stage trim
        this.ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
        this.ctx.fillRect(leftWallX, floorY + wallThickness * 0.15, rightWallX - leftWallX, Math.max(2, wallThickness * 0.18));
        this.ctx.fillStyle = '#ff77bc';
        this.ctx.fillRect(leftWallX, floorY, rightWallX - leftWallX, Math.max(2, wallThickness * 0.12));
    }

    /** Render conveyor belt */
    renderConveyor(x: number, y: number, width: number, height: number, offset: number, chevronSpacing?: number, chevronSize?: number): void {
        this.ctx.fillStyle = Colors.conveyor;
        this.ctx.fillRect(x, y, width, height);
        this.ctx.fillStyle = 'rgba(255,255,255,0.08)';
        this.ctx.fillRect(x, y + height * 0.08, width, height * 0.2);

        // Animated chevron pattern - use provided values or defaults
        const spacing = chevronSpacing ?? 20;
        const size = chevronSize ?? 8;
        
        this.ctx.strokeStyle = '#ff77bc';
        this.ctx.lineWidth = 2;
        const chevronCount = Math.ceil(width / spacing) + 2;
        
        this.ctx.save();
        this.ctx.beginPath();
        this.ctx.rect(x, y, width, height);
        this.ctx.clip();

        for (let i = 0; i < chevronCount; i++) {
            const cx = x + (i * spacing) + (offset % spacing);
            this.ctx.beginPath();
            this.ctx.moveTo(cx, y);
            this.ctx.lineTo(cx + size, y + height / 2);
            this.ctx.lineTo(cx, y + height);
            this.ctx.stroke();
        }
        this.ctx.restore();
    }

    /** Render FPS counter */
    renderFps(fps: number): void {
        this.ctx.fillStyle = '#00FF00';
        this.ctx.font = '14px monospace';
        this.ctx.fillText(`FPS: ${fps}`, 10, 20);
    }

    /** Render spring line from grab point to target */
    renderSpringLine(line: { fromX: number; fromY: number; toX: number; toY: number }): void {
        this.ctx.save();
        
        // Draw dashed line
        this.ctx.setLineDash([6, 4]);
        this.ctx.strokeStyle = Colors.highlight;
        this.ctx.lineWidth = 2;
        this.ctx.globalAlpha = 0.7;
        
        this.ctx.beginPath();
        this.ctx.moveTo(line.fromX, line.fromY);
        this.ctx.lineTo(line.toX, line.toY);
        this.ctx.stroke();
        
        // Draw target circle
        this.ctx.setLineDash([]);
        this.ctx.beginPath();
        this.ctx.arc(line.toX, line.toY, 6, 0, Math.PI * 2);
        this.ctx.stroke();
        
        // Draw grab point
        this.ctx.fillStyle = Colors.highlight;
        this.ctx.beginPath();
        this.ctx.arc(line.fromX, line.fromY, 4, 0, Math.PI * 2);
        this.ctx.fill();
        
        this.ctx.restore();
    }

    /** Render debug info */
    renderDebugInfo(info: string[]): void {
        this.ctx.fillStyle = '#00FF00';
        this.ctx.font = '12px monospace';
        info.forEach((line, i) => {
            this.ctx.fillText(line, 10, 40 + i * 16);
        });
    }
}
